import { useState, useEffect, ChangeEvent } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import {
  AdvancedWeightContent,
  AdvancedWeightRefContent,
  AutocompleteInput,
  GeocodeInput,
  Button,
  Checkbox,
  FilterItemWrapper,
  Popup
} from '@/components';
import { useProjectContext } from '@/context/ProjectContext';
import {
  ButtonColor,
  ButtonSize,
  Icons,
  SearchParams,
  SoldWeightParams,
  AdsWeightParams,
  Weights,
  MarketFilterParams,
  TabsType
} from '@/enums';
import { filterItems as soldFilterItems, weightItems as soldWeightItems } from '../pages/sold/filterObjects';
import { filterItems as adsFilterItems, weightItems as adsWeightItems } from '../pages/ads/filterObjects';
import strings from '@/language/Swedish';
import {
  Items,
  WeigthsWrapper,
} from '@/styles/FilterStyles';
import {
  CoordinatesType,
  SoldRefDataType,
  SoldWeightDataType,
} from '@/types/dataTypes';
import { defaultSoldRefParams } from '@/context/SoldDefault';
import { SEARCH_TYPES } from '@/constants';

const SearchInputContainer = styled.div`
  ${tw`relative flex flex-1 gap-2 items-center md:pt-0 pt-4`};
  z-index: 10;
`;
const HeadingMobile = styled.h2`
  ${tw`mb-2 text-slate-700 sm:block md:hidden`}
`;

const HeadingDesktop = styled.h2`
  ${tw`mb-2 text-slate-700 hidden md:block`}
`;

const CenteredButtonContainer = styled.div`
  ${tw`flex justify-center items-center w-full`}
`;

const RadioButton = styled.input`
  accent-color: green;
`

const ItemsContainer = styled.div`

`;

interface Props<TFilterData> {
  handleSearch: (coordinates?: CoordinatesType) => void;
  filterData: TFilterData;
  setMultipleParam: (state: SearchParams, label: string, value: any) => void;
  handleResetFilter: () => void;
  setMarketParam: (state: SearchParams, label: string, value: any) => void;
}

const AdvancedFilter = <
  TFilterParams extends string,
  TFilterData extends Record<string, any>
>({
  handleSearch,
  setMultipleParam,
  filterData,
  handleResetFilter,
  setMarketParam
}: Props<TFilterData>) => {
  const [coordinates, setCoordinates] = useState<CoordinatesType | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [searchType, setSearchtype] = useState<string>(SEARCH_TYPES.ADRESS);
  const { isAdvancedFilterOpen, setIsAdvancedFilterOpen, project, selectedTab } =
    useProjectContext();

  // Use different filter items and weight items based on current tab
  const isAdsTab = selectedTab === TabsType.ads;
  const filterItems = isAdsTab ? adsFilterItems : soldFilterItems;
  const weightItems = isAdsTab ? adsWeightItems : soldWeightItems;
  
  // Use different weight params based on current tab
  const contractDateWeightParam = isAdsTab ? AdsWeightParams.registered_date_weight : SoldWeightParams.contract_date_weight;
  const distanceWeightParam = isAdsTab ? AdsWeightParams.distance_weight : SoldWeightParams.distance_weight;
  
  // Use different label for contract date based on current tab
  const contractDateLabel = isAdsTab ? strings.filter.ads_date : strings.filter.contract_date;

  const handleCheckboxClick = (itemId: string, checkboxId: string) => {
    const selectedItems = filterData.filterData[
      itemId as TFilterParams
    ] as string[];

    let values;
    if (selectedItems && selectedItems.length > 0) {
      values = selectedItems.includes(checkboxId)
        ? selectedItems.filter((id) => id !== checkboxId)
        : [...selectedItems, checkboxId];
    } else values = [checkboxId];

    if (values?.length === 0) {
      setMultipleParam(SearchParams.filter, itemId, null);
    } else {
      setMultipleParam(SearchParams.filter, itemId, values);
    }

    if (itemId == MarketFilterParams.homeType) {
      setMarketParam(SearchParams.filter, itemId, values);
    }
    else if (itemId == MarketFilterParams.ownership_type) {
      setMarketParam(SearchParams.filter, itemId, values);
    }
  };

  const handleWeightChange = (keyEl: string, value: number) => {
    setMultipleParam(SearchParams.weight, keyEl, value);
  };

  const handleRefValueChange = (keyEl: string, value: number) => {
    setMultipleParam(SearchParams.ref, keyEl, value);
  };

  const handleReset = (keyWeightEl: string, keyRefEl?: string) => {
    handleWeightChange(keyWeightEl, Weights.none);
    if (keyRefEl) {
      handleRefValueChange(keyRefEl, 0);
    }
  };

  const handleDefault = (keyWeightEl: string, keyRefEl?: string) => {
    handleWeightChange(keyWeightEl, Weights.medium);
    if (keyRefEl) {
      handleRefValueChange(
        keyRefEl,
        defaultSoldRefParams[keyRefEl as keyof SoldRefDataType] || 0
      );
    }
  };

  const isChecked = (itemId: string, checkboxId: string) => {
    const items = filterData.filterData[itemId as TFilterParams] as string[];
    if (!items) {
      return false;
    }
    return items.includes(checkboxId);
  };

  const getIsActive = (keyWeightEl: string, keyRefEl?: string) => {
    const weightValue =
      filterData.weightData[keyWeightEl as keyof SoldWeightDataType];
    const refValue =
      filterData.refData[keyRefEl as keyof SoldRefDataType] || undefined;

    if (
      (weightValue && weightValue !== Weights.none) ||
      (refValue && refValue !== Weights.none)
    )
      return true;
    else return false;
  };

  const getNoSelectedItems = (itemId: string) => {
    const selectedItems = filterData.filterData[
      itemId as TFilterParams
    ] as string[];

    return !!selectedItems;
  };

  const onPlaceSelected = (coordinates: CoordinatesType) => {
    setCoordinates(coordinates);
    handleSearch(coordinates ? coordinates : undefined);
  };

  useEffect(() => {
    if (isAdvancedFilterOpen) {
      setCoordinates(null);
    }
  }, [isAdvancedFilterOpen]);

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true);
    }, 3000);
  }, []);

  const closeSlot = (
    <>
      <Button
        icon={Icons.refresh}
        size={ButtonSize.medium}
        color={ButtonColor.transparent}
        onClick={handleResetFilter}
      >
        {strings.data.reset}
        </Button>
      <Button
        icon={Icons.remove}
        size={ButtonSize.medium}
        color={ButtonColor.transparent}
        onClick={() => {
          handleSearch(coordinates ? coordinates : undefined);
          setIsAdvancedFilterOpen(false);
        }}
      />
    </>
  )
  return (
    <Popup contentPosition="bottom" closeSlot={closeSlot} isOpen={isAdvancedFilterOpen} setIsOpen={setIsAdvancedFilterOpen}>
      <div className="flex items-center flex-col">
        <HeadingMobile>{strings.report.about}</HeadingMobile>
        {searchType === SEARCH_TYPES.ADRESS && <SearchInputContainer>
          {loaded && (<AutocompleteInput
            onPlaceSelected={(coordinates) =>
              onPlaceSelected(coordinates)
            }
            project={project}
          />)}
        </SearchInputContainer>}
        {searchType === SEARCH_TYPES.PROPERTY && <SearchInputContainer>
          <GeocodeInput
            onPlaceSelected={(coordinates) =>
              onPlaceSelected(coordinates)
            }
            project={project}
          />
        </SearchInputContainer>
        }
        <fieldset className="flex flex-1 gap-4 mt-2 md:pb-0 pb-4">
          <div className="flex gap-2">
            <RadioButton type="radio" id="contact-adress" name={SEARCH_TYPES.ADRESS} value={SEARCH_TYPES.ADRESS} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchtype(e.target.value)} checked={searchType === SEARCH_TYPES.ADRESS} />
            <label htmlFor="contact-adress">{SEARCH_TYPES.ADRESS}</label>
          </div>
          <div className="flex gap-2">
            <RadioButton type="radio" id="contact-property" name={SEARCH_TYPES.PROPERTY} value={SEARCH_TYPES.PROPERTY} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchtype(e.target.value)} checked={searchType == SEARCH_TYPES.PROPERTY} />
            <label htmlFor="contact-property">{SEARCH_TYPES.PROPERTY}</label>
          </div>
        </fieldset>
        <ItemsContainer>
          <HeadingDesktop>{strings.report.about}</HeadingDesktop>
          <Items>
            {filterItems.tagItems.map((item, index) => (
              <FilterItemWrapper
                key={`filter-item-${index}`}
                icon={item.icon}
                label={item.label}
                handleReset={() =>
                  setMultipleParam(SearchParams.filter, item.id, null)
                }
                isActive={getNoSelectedItems(item.id)}>
                <div>
                  {item.items.map((checkboxItem, index) => (
                    <Checkbox
                      className="mr-2"
                      id={checkboxItem.id}
                      key={`checkbox-${index}`}
                      label={checkboxItem.label}
                      isChecked={isChecked(item.id, checkboxItem.id)}
                      onToggle={() =>
                        handleCheckboxClick(item.id, checkboxItem.id)
                      }
                    />
                  ))}
                </div>
              </FilterItemWrapper>
            ))}
            <WeigthsWrapper
              $isActive={
                getIsActive(contractDateWeightParam) ||
                getIsActive(distanceWeightParam)
              }>
              <AdvancedWeightContent
                label={contractDateLabel}
                weightValue={filterData.weightData[contractDateWeightParam]}
                keyWeightEl={contractDateWeightParam}
                handleWeightValueChange={handleWeightChange}
              />
              <AdvancedWeightContent
                label={strings.filter.distance}
                weightValue={filterData.weightData.distance_weight}
                keyWeightEl={distanceWeightParam}
                handleWeightValueChange={handleWeightChange}
              />
            </WeigthsWrapper>
            {weightItems
              .filter(
                ({ keyWeightEl }) =>
                  keyWeightEl !== contractDateWeightParam &&
                  keyWeightEl !== distanceWeightParam
              )
              .map((item, index) => (
                <div key={`filter-${index}`}>
                  <FilterItemWrapper
                    key={item.keyWeightEl}
                    icon={item.icon}
                    label={item.label}
                    isActive={getIsActive(
                      item.keyWeightEl,
                      item.keyRefEl
                    )}
                    handleReset={() =>
                      handleReset(item.keyWeightEl, item.keyRefEl)
                    }>
                    {getIsActive(item.keyWeightEl, item.keyRefEl) ? (
                      <AdvancedWeightRefContent
                        weightValue={
                          filterData.weightData[item.keyWeightEl]
                        }
                        refValue={
                          filterData.refData[
                          item.keyRefEl as keyof SoldRefDataType
                          ] || undefined
                        }
                        inputMin={item.min}
                        inputMax={item.max}
                        unit={item.unit}
                        keyRefEl={item.keyRefEl}
                        keyWeightEl={item.keyWeightEl}
                        handleRefValueChange={handleRefValueChange}
                        handleWeightValueChange={handleWeightChange}
                      />
                    ) : (
                      <CenteredButtonContainer>
                        <Button
                          color={ButtonColor.primary}
                          size={ButtonSize.large}
                          onClick={() =>
                            handleDefault(item.keyWeightEl, item.keyRefEl)
                          }>
                          {strings.filter.activate_about_filter}
                        </Button>
                      </CenteredButtonContainer>
                    )}
                  </FilterItemWrapper>
                </div>
              ))}
          </Items>
        </ItemsContainer>
      </div>
    </Popup>
  );
};

export default AdvancedFilter;
